import { useState, useMemo } from 'react';
import type { Migration } from '../../types';
import { sortByKey } from '../../utils/sort';

// Type-safe sortable fields
export type SortableField =
  | 'name'
  | 'type'
  | 'createdBy'
  | 'sourceOrganization'
  | 'targetOrganization'
  | 'lastRun'
  | 'createdAtUtc'
  | 'status';

export const useSortedJobs = (jobs: Migration[]) => {
  const [sortBy, setSortBy] = useState<SortableField>('createdAtUtc');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const sortedJobs = useMemo(() => {
    // Handle date fields
    if (sortBy === 'lastRun' || sortBy === 'createdAtUtc') {
      return sortByKey(
        jobs,
        (j) => {
          const created = new Date(j.createdAtUtc);
          const updated = j.updatedAtUtc ? new Date(j.updatedAtUtc) : created;
          return updated > created ? updated : created;
        },
        sortDir
      );
    }

    // Map UI field names to Migration properties
    if (sortBy === 'sourceOrganization') {
      return sortByKey(jobs, 'sourceOrgName', sortDir);
    }

    if (sortBy === 'targetOrganization') {
      return sortByKey(jobs, 'targetOrgName', sortDir);
    }

    // Map 'type' to display string for proper sorting
    if (sortBy === 'type') {
      return sortByKey(jobs, (j) => (j.isSnapshot ? 'Snapshot' : 'Migration'), sortDir);
    }

    // default: use sortByKey for string fields
    return sortByKey(jobs, sortBy as keyof Migration, sortDir);
  }, [jobs, sortBy, sortDir]);

  return { sortedJobs, sortBy, setSortBy, sortDir, setSortDir };
};

export default useSortedJobs;
